import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';

import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DesignerLeftPanelMetricsComponent from 'webapp/components/designer/left-panel/metrics';
import DesignerLeftPanelVisualizationsComponent from 'webapp/components/designer/left-panel/visualizations';
import DesignerPanelSectionComponent from 'webapp/components/designer/panel-section';
import ToolTipComponent from 'webapp/components/tool-tip';

import type Board from 'webapp/models-custom/board';
import type BoardState from 'webapp/models-custom/board-state';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';

import { DesignerTourLocation } from 'webapp/services/flow/tour/designer';
import type { NewBlockData } from 'webapp/services/model-manager/blocks';

export interface Args {
	boardState: BoardState | null;
	isOpen: boolean;
	onPanelToggle: (isOpen: boolean) => void;
	activeDatawallGrid: DatawallGrid | null;
	preselectedSourceFromWizard: string;
	setNewBlockPositionAndSize: (blockData: NewBlockData, callback?: () => void) => void;
}

export default class DesignerLeftPanelComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked isSectionVisualizationOpen = false;

	@tracked isAllMetricInfoLoaded = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.eventManagerService.on(AppEvent.OPEN_LEFT_SIDEBAR, this, (eventData) => {
			next(this, () => {
				this.openPanel();

				if (eventData.isFinal) {
					this.isSectionVisualizationOpen = true;
					this.flowTourDesignerService.startTour(eventData.tourId);
				} else {
					this.flowTourDesignerService.startTour(eventData.tourId);
				}
			});
			return eventData.isFinal;
		});

		void this.mmMetricsService.prepareForMetricEditing().then(() => {
			this.isAllMetricInfoLoaded = true;
		});
	}

	get board() {
		return (this.args.boardState?.board as Board) || null;
	}

	get tooltipText() {
		return this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
			? 'Library'
			: 'Databox library';
	}

	@action
	openPanel() {
		this.args.onPanelToggle(true);
	}

	closePanel() {
		this.args.onPanelToggle(false);

		this.flowTourDesignerService.endActiveTourAtLocation(DesignerTourLocation.LEFT_PANEL);
	}

	checkForTourStart() {
		this.flowTourDesignerService.endHiddenTour();
		this.flowTourDesignerService.checkTour();
	}

	@action
	togglePanel() {
		if (this.args.isOpen) {
			this.closePanel();
		} else {
			this.openPanel();
		}
		this.checkForTourStart();
	}

	@action
	toggleSectionVisualization(): void {
		this.isSectionVisualizationOpen = !this.isSectionVisualizationOpen;
		schedule('afterRender', () => {
			document
				.querySelector('.designer-panel--left .metric-list')
				?.dispatchEvent(new Event('resize'));
		});

		if (this.isSectionVisualizationOpen) {
			this.checkForTourStart();
		}
	}

	@action
	doNothing(): void {
		// as the method name says
	}

	override willDestroy(): void {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.OPEN_LEFT_SIDEBAR, this);
	}
	<template>
		<div
			class="designer-panel designer-panel--left {{unless @isOpen 'designer-panel--closed'}}"
		>

			{{#if this.board}}

				<a role="button" class="panel-toggler" {{on "click" this.togglePanel}}>
					<CommonUiIconComponent @name="DatablockLibrary" @size="lg" />
					<ToolTipComponent @text={{this.tooltipText}} />
				</a>

				<div class="designer-panel__inner">

					{{! <div class="designer-panel__bg"></div> }}

					<DesignerPanelSectionComponent
						@title="Visualization types"
						@index={{1}}
						@isOpen={{this.isSectionVisualizationOpen}}
						@toggle={{this.toggleSectionVisualization}}
					>
						<DesignerLeftPanelVisualizationsComponent
							@isOpen={{this.isSectionVisualizationOpen}}
							@isPanelOpen={{@isOpen}}
							@board={{this.board}}
							@activeDatawallGrid={{@activeDatawallGrid}}
							@setNewBlockPositionAndSize={{@setNewBlockPositionAndSize}}
						/>
					</DesignerPanelSectionComponent>

					<DesignerPanelSectionComponent
						@title="Metric library"
						@index={{3}}
						{{! three to not match w/ right panel section2 which has some specific css rules }}
						@isOpen={{true}}
						@toggle={{this.doNothing}}
					>
						{{#if this.isAllMetricInfoLoaded}}
							<DesignerLeftPanelMetricsComponent
								@isPanelOpen={{@isOpen}}
								@board={{this.board}}
								@activeDatawallGrid={{@activeDatawallGrid}}
								@preselectedSource={{@preselectedSourceFromWizard}}
								@setNewBlockPositionAndSize={{@setNewBlockPositionAndSize}}
							/>
						{{else}}
							<BarLoaderComponent />
						{{/if}}
					</DesignerPanelSectionComponent>

				</div>

			{{/if}}

		</div>
	</template>
}
